import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Container,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Avatar,
  Zoom,
  Backdrop,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

const PosterManagement = () => {
  const [posters, setPosters] = useState([]);
  const [filteredPosters, setFilteredPosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPoster, setSelectedPoster] = useState(null);
  const [userTypeFilter, setUserTypeFilter] = useState('all');
  
  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Image preview dialog
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageData, setPreviewImageData] = useState(null);

  const token = localStorage.getItem('accessToken');

  // Canvas dimensions from editor (MUST match ImageEditor)
  const CANVAS_WIDTH = 600;
  const CANVAS_HEIGHT = 800;

  useEffect(() => {
    fetchPosters();
  }, []);

  useEffect(() => {
    if (userTypeFilter === 'all') {
      setFilteredPosters(posters);
    } else {
      setFilteredPosters(
        posters.filter(poster => {
          if (Array.isArray(poster.usertype)) {
            return poster.usertype.includes(userTypeFilter);
          }
          return poster.usertype === userTypeFilter;
        })
      );
    }
    setPage(0);
  }, [userTypeFilter, posters]);

  const fetchPosters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        'https://erpapi.eduskillsfoundation.org/documents/posters2'
      );

      console.log('Raw API response:', response.data);

      const postersWithParsedLinks = response.data.map(poster => {
        if (typeof poster.usertype === 'string' && poster.usertype.startsWith('[')) {
          try {
            poster.usertype = JSON.parse(poster.usertype);
          } catch (e) {
            console.error('Failed to parse usertype:', e);
          }
        }

        if (poster.description) {
          if (typeof poster.description === 'string') {
            try {
              poster.linkData = JSON.parse(poster.description);
              console.log('Parsed linkData for poster', poster.posterid, ':', poster.linkData);
            } catch (e) {
              console.error('Failed to parse description:', e);
              poster.linkData = null;
            }
          }
        } else {
          poster.linkData = null;
        }

        return poster;
      });

      console.log('Posters with parsed links:', postersWithParsedLinks);
      setPosters(postersWithParsedLinks);
      setError(null);
    } catch (err) {
      setError('Failed to fetch posters. Please try again.');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (poster) => {
    setSelectedPoster(poster);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPoster(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.post(
        'https://erpapi.eduskillsfoundation.org/documents/deletePosters2',
        { posterid: selectedPoster.posterid },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setPosters(posters.filter(poster => poster.posterid !== selectedPoster.posterid));
      handleCloseDialog();
    } catch (err) {
      setError('Failed to delete poster. Please try again.');
      console.error('Delete error:', err);
      handleCloseDialog();
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleImagePreview = (poster) => {
    setPreviewImageData(poster);
    setImagePreviewOpen(true);
  };

  const handleCloseImagePreview = () => {
    setImagePreviewOpen(false);
    setPreviewImageData(null);
  };

  const getUserTypeColor = (userType) => {
    const colors = {
      student: 'primary',
      login: 'secondary',
      institute: 'success',
      staff: 'warning',
    };
    return colors[userType] || 'default';
  };

  const hasLinks = (poster) => {
    return (
      poster.linkData &&
      Array.isArray(poster.linkData) &&
      poster.linkData.length > 0
    );
  };

  const formatUserType = (usertype) => {
    if (Array.isArray(usertype)) {
      return usertype;
    }
    return [usertype];
  };

  const getUserTypeChips = (usertype) => {
    const types = formatUserType(usertype);
    return types.map((type, index) => (
      <Chip
        key={index}
        label={type}
        size="small"
        color={getUserTypeColor(type)}
        sx={{
          fontWeight: 600,
          textTransform: 'capitalize',
          fontSize: '0.75rem',
          height: '24px',
          mr: 0.5,
          mb: 0.5,
        }}
      />
    ));
  };

  const paginatedPosters = filteredPosters.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
        sx={{ backgroundColor: '#f5f5f5' }}
      >
        <CircularProgress size={50} />
        <Typography variant="body1" sx={{ mt: 2, color: '#666' }}>
          Loading Posters...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            pb: 2,
            borderBottom: '2px solid #e0e0e0',
            backgroundColor: 'white',
            padding: 3,
            borderRadius: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: '#333',
              fontWeight: 700,
              mb: 1,
            }}
          >
            📊 Poster Management - Table View
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#666',
            }}
          >
            View, filter, and manage all posters in a structured table format
          </Typography>
        </Box>

        {/* Filter Section */}
        <Paper
          sx={{
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            padding: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FilterListIcon sx={{ color: '#666' }} />
            <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, mr: 1 }}>
              Filter by User Type:
            </Typography>
            {['all', 'student', 'login', 'institute', 'staff'].map(filter => (
              <Chip
                key={filter}
                label={filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
                color={userTypeFilter === filter ? 'primary' : 'default'}
                onClick={() => setUserTypeFilter(filter)}
                sx={{
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: userTypeFilter === filter ? '#1976d2' : '#e0e0e0',
                  color: userTypeFilter === filter ? 'white' : '#666',
                  '&:hover': {
                    backgroundColor: userTypeFilter === filter ? '#1565c0' : '#d0d0d0',
                  },
                }}
              />
            ))}
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Chip
              icon={<ImageIcon />}
              label={`${filteredPosters.length} Poster${filteredPosters.length !== 1 ? 's' : ''}`}
              color="primary"
              variant="outlined"
            />
          </Box>
        </Paper>

        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Table - WITHOUT Links Column */}
        <TableContainer 
          component={Paper} 
          sx={{ 
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: 2,
          }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="poster table">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  Poster ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  Preview
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  Image URL
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  User Type
                </TableCell>
                {/* REMOVED: Links Column */}
                <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedPosters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <ImageIcon sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                      <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
                        No posters found
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#999' }}>
                        {userTypeFilter === 'all'
                          ? 'No posters available. Upload some posters to get started.'
                          : `No posters found for "${userTypeFilter}". Try a different filter.`}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedPosters.map((poster) => (
                  <TableRow
                    key={poster.posterid}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f9f9f9',
                      },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    {/* Poster ID */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                        #{poster.posterid}
                      </Typography>
                    </TableCell>

                    {/* Image Preview */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={poster.imageurl}
                          alt={`Poster ${poster.posterid}`}
                          variant="rounded"
                          sx={{
                            width: 48,
                            height: 60,
                            cursor: 'pointer',
                            border: '2px solid #e0e0e0',
                            transition: 'transform 0.2s',
                            '&:hover': {
                              transform: 'scale(1.1)',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            },
                          }}
                          onClick={() => handleImagePreview(poster)}
                        />
                        <Tooltip title="View Fullscreen">
                          <IconButton
                            size="small"
                            onClick={() => handleImagePreview(poster)}
                            sx={{
                              backgroundColor: '#f0f0f0',
                              '&:hover': {
                                backgroundColor: '#1976d2',
                                color: 'white',
                              },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>

                    {/* Image URL */}
                    <TableCell>
                      <Tooltip title={poster.imageurl}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 250,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#666',
                            fontSize: '0.813rem',
                          }}
                        >
                          {poster.imageurl}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* User Type */}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {getUserTypeChips(poster.usertype)}
                      </Box>
                    </TableCell>

                    {/* REMOVED: Links Cell */}

                    {/* Actions */}
                    <TableCell align="center">
                      <Tooltip title="Delete Poster">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDeleteClick(poster)}
                          sx={{
                            transition: 'all 0.2s',
                            '&:hover': {
                              transform: 'scale(1.15)',
                              backgroundColor: 'rgba(211, 47, 47, 0.1)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {filteredPosters.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={filteredPosters.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid #e0e0e0',
                backgroundColor: '#fafafa',
              }}
            />
          )}
        </TableContainer>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <DialogContentText>
            Are you sure you want to delete poster <strong>#{selectedPoster?.posterid}</strong>? 
            This action cannot be undone.
            {selectedPoster && hasLinks(selectedPoster) && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#e65100' }}>
                  ⚠️ This poster contains {selectedPoster.linkData.length} clickable
                  link(s) that will also be deleted.
                </Typography>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{ color: '#666', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{ fontWeight: 600 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={handleCloseImagePreview}
        maxWidth={false}
        fullScreen
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.95)',
            },
          },
        }}
      >
        <Zoom in={imagePreviewOpen}>
          <DialogContent
            sx={{
              padding: 0,
              position: 'relative',
              overflow: 'hidden',
              height: '100vh',
              width: '100vw',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#000',
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleCloseImagePreview}
              sx={{
                position: 'absolute',
                top: 20,
                right: 20,
                zIndex: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#000',
                width: 56,
                height: 56,
                boxShadow: 3,
                '&:hover': {
                  backgroundColor: '#fff',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <CloseIcon fontSize="large" />
            </IconButton>

            {/* Large Preview Image - 600x800px */}
            {previewImageData && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  maxWidth: '90vw',
                  maxHeight: '90vh',
                }}
              >
                <img
                  src={previewImageData.imageurl}
                  alt={`Poster ${previewImageData.posterid}`}
                  onClick={handleCloseImagePreview}
                  style={{
                    width: '600px',
                    height: '800px',
                    objectFit: 'contain',
                    cursor: 'zoom-out',
                    imageRendering: 'high-quality',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    borderRadius: '8px',
                  }}
                />

                {/* Info Overlay */}
                <Box
                  sx={{
                    mt: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                    }}
                  >
                    Poster ID: #{previewImageData.posterid}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#bbb',
                    }}
                  >
                    |
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#bbb',
                    }}
                  >
                    600 × 800 px
                  </Typography>
                  {hasLinks(previewImageData) && (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#bbb',
                        }}
                      >
                        |
                      </Typography>
                      <Chip
                        icon={<LinkIcon sx={{ fontSize: '0.9rem', color: 'white' }} />}
                        label={`${previewImageData.linkData.length} Link${previewImageData.linkData.length > 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(25, 118, 210, 0.9)',
                          color: 'white',
                          fontWeight: 600,
                        }}
                      />
                    </>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
        </Zoom>
      </Dialog>
    </Box>
  );
};

export default PosterManagement;
